// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import { doSignOut, type User } from '@evolvemep/api-ts';
import { Title } from '@mantine/core';
// oxlint-disable-next-line no-restricted-imports
import { useNavigate } from 'react-router';

import { EULAModal } from 'app/EULAModal';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { setLocalStorageWrapped } from 'hooks/useLocalStorage';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

import { useConfirmUserSso, useGetTokenSso } from '../auth/restCalls';
import { extractSsoStateVar } from './helper';

export const LoginMicrosoftSSO = () => {
  const { refreshUser } = useMaybeUser();
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const { confirmUserSso } = useConfirmUserSso();
  const { getTokenSso } = useGetTokenSso();
  const myUrl = new URL(window.location.href.replace(/#/g, '?'));
  const ssoCode = myUrl.searchParams.get('code');
  const state = extractSsoStateVar(myUrl.searchParams.get('state') ?? '');
  const redirect = state.previousLoc ?? '/';

  useEffect(() => {
    const checkEulaAccepted = async (user: User) => {
      if (isNil(user.confirmedAt)) {
        doSignOut('/auth/login?errorCode=EmailNotVerified');
        return;
      }

      if (user.acceptedLatestEula) {
        void navigate(redirect);
      }
    };

    const removeCodeParamFromUrl = () => {
      const searchParams = new URLSearchParams(location.search);

      // Remove the specific parameter
      searchParams.delete('code');

      // Update the URL without the removed parameter
      void navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
    };

    if (isNotNil(ssoCode)) {
      removeCodeParamFromUrl();
      getTokenSso(ssoCode)
        .then(async (authResult) => {
          localStorage.setItem('STAY_SIGNED_IN', 'true');
          setLocalStorageWrapped('EVOLVE_ACCESS_TOKEN', authResult.accessToken);
          setLocalStorageWrapped('EVOLVE_REFRESH_TOKEN', authResult.refreshToken);
          setLocalStorageWrapped('EVOLVE_TOKEN_USERNAME', authResult.tokenUserName);

          const { userId, confirmationCode } = state;

          if (isNotNil(userId) && isNotNil(confirmationCode)) {
            await confirmUserSso({
              userName: authResult.tokenUserName,
              userId,
              confirmationCode,
            }).catch((e) => {
              // oxlint-disable-next-line no-console
              console.error('Could not accept invite directly, user is likely not confirmed.', e);
            });
          }
          const user = await refreshUser();

          if (isNil(user)) {
            doSignOut('/auth/login?errorCode=UserNotFoundException');
            return;
          }

          await checkEulaAccepted(user);
        })
        .catch((error) => {
          // oxlint-disable-next-line no-console
          console.error('Could not make the authentication by the sso authentication code.', error);
          doSignOut('/auth/login?errorCode=AuthorizationCodeInvalidOrExpired');
        });
    }
  }, [confirmUserSso, getTokenSso, navigate, redirect, refreshUser, ssoCode, state]);

  return (
    <>
      <Title fz="h3">Authenticating, please wait!</Title>
      <EULAModal onEulaAccepted={() => navigate(redirect)} />
    </>
  );
};
